import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { SidebarLayout, Card } from '../components/layout';
import { Select } from '../components/ui/form';
import { JsonInputPanel, SimulationResultsPanel, RecommendationsPanel, DraftWorkflowBuilder, CreateOrderAttributeModal, AddRoutingRuleToWorkflowModal } from '../components/simulator';
import type { DraftWorkflowState, DraftIdentifier, DraftRoutingRule } from '../components/simulator';
import { useDivisionContext } from '../contexts/division-context';
import { useWorkflowSimulator } from '../hooks/use-workflow-simulator';
import { useCreateDivisionWorkflow } from '../hooks/use-division-workflows';
import { useCreateMatchIdentifier } from '../hooks/use-match-identifiers';
import { useCreateWorkflowMatchIdentifier } from '../hooks/use-workflow-match-identifiers';
import { useOrderDetail } from '../hooks/use-orders';
import { useAuth } from '../hooks/use-auth';
import { FullSimulationResult, WorkflowRecommendationResult, CreateWorkflowRequest, CreateMatchIdentifierRequest, DiscoveredField } from '../types';
import { cn } from '../utils';

export function SimulatorPage() {
  const { divisionId: urlDivisionId } = useParams<{ divisionId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isSupport } = useAuth();
  
  // Use the global division context
  const { selectedDivisionId, setSelectedDivisionId, divisions } = useDivisionContext();

  // Get orderId from URL query params
  const orderIdFromUrl = searchParams.get('orderId');

  // JSON input state
  const [jsonInput, setJsonInput] = useState('');
  const [totalAmount, setTotalAmount] = useState<string>('');
  
  // Mode toggle
  const [mode, setMode] = useState<'simulate' | 'recommend'>('simulate');
  
  // Simulation result state
  const [simulationResult, setSimulationResult] = useState<FullSimulationResult | null>(null);
  const [recommendationResult, setRecommendationResult] = useState<WorkflowRecommendationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Draft workflow state for in-page workflow builder
  const [draftWorkflow, setDraftWorkflow] = useState<DraftWorkflowState>({
    name: '',
    description: '',
    identifiers: [],
    routingRules: [],
    workflowSteps: [],
    isExpanded: false,
  });

  // Order attribute creation modal state
  const [orderAttributeModalOpen, setOrderAttributeModalOpen] = useState(false);
  const [selectedFieldForAttribute, setSelectedFieldForAttribute] = useState<DiscoveredField | null>(null);

  // Add routing rule to workflow modal state
  const [routingRuleModalOpen, setRoutingRuleModalOpen] = useState(false);
  const [selectedFieldForRoutingRule, setSelectedFieldForRoutingRule] = useState<DiscoveredField | null>(null);

  // Get simulator hook
  const { simulateFull, isFullLoading, getRecommendations, isRecommendationsLoading, reset } = useWorkflowSimulator(selectedDivisionId || '');

  // Get workflow creation hooks
  const createWorkflowMutation = useCreateDivisionWorkflow(selectedDivisionId || '');
  const createMatchIdentifierMutation = useCreateMatchIdentifier();
  const createWorkflowMatchIdentifierMutation = useCreateWorkflowMatchIdentifier();

  // Fetch order details if orderId is in URL
  const { data: orderDetail, isLoading: isOrderLoading } = useOrderDetail(
    selectedDivisionId || '',
    orderIdFromUrl || '',
    !!selectedDivisionId && !!orderIdFromUrl
  );

  // Sync selectedDivisionId with URL param
  useEffect(() => {
    if (urlDivisionId && urlDivisionId !== selectedDivisionId) {
      setSelectedDivisionId(urlDivisionId);
    }
  }, [urlDivisionId, selectedDivisionId, setSelectedDivisionId]);

  // Populate JSON input from navigation state if provided
  useEffect(() => {
    const state = location.state as { jsonData?: string; orderNumber?: string } | null;
    if (state?.jsonData) {
      setJsonInput(state.jsonData);
      // Clear the navigation state to prevent re-population on future navigations
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // Populate JSON input from order detail when orderId is in URL
  useEffect(() => {
    if (orderDetail?.orderDocument) {
      try {
        // Pretty-print the JSON data for better readability
        const formattedJson = JSON.stringify(orderDetail.orderDocument, null, 2);
        setJsonInput(formattedJson);
        
        // Also set total amount if available
        if (orderDetail.totalAmount) {
          setTotalAmount(orderDetail.totalAmount.toString());
        }
      } catch (err) {
        console.error('Failed to parse order JSON data:', err);
        setError('Failed to load order data');
      }
    }
  }, [orderDetail]);

  // Clear results when division changes
  useEffect(() => {
    setSimulationResult(null);
    setRecommendationResult(null);
    setError(null);
    reset();
  }, [selectedDivisionId]);

  // Update URL when division selection changes
  const handleDivisionChange = (newDivisionId: string) => {
    setSelectedDivisionId(newDivisionId);
    if (newDivisionId) {
      navigate(`/simulator/${newDivisionId}`);
    } else {
      navigate('/simulator');
    }
  };

  const handleRunSimulation = async () => {
    if (!selectedDivisionId || !jsonInput.trim()) return;

    setError(null);
    
    try {
      const jsonData = JSON.parse(jsonInput);
      
      if (mode === 'simulate') {
        const result = await simulateFull({
          jsonData,
          totalAmount: totalAmount ? parseFloat(totalAmount) : undefined,
          includeDetailedMatches: true
        });
        setSimulationResult(result);
        setRecommendationResult(null);
      } else {
        const result = await getRecommendations({
          orderData: jsonData,
          includeMatchingRecommendations: true,
          includeApprovalRecommendations: true,
          includeSkipConditionRecommendations: true
        });
        setRecommendationResult(result);
        setSimulationResult(null);
      }
    } catch (err: any) {
      if (err instanceof SyntaxError) {
        setError('Invalid JSON syntax');
      } else {
        setError(err?.message || err?.apiError?.message || 'Operation failed');
      }
      setSimulationResult(null);
      setRecommendationResult(null);
    }
  };

  // Handler for getting recommendations after a simulation with no match
  const handleGetRecommendationsAfterNoMatch = async () => {
    if (!selectedDivisionId || !jsonInput.trim()) return;

    try {
      const jsonData = JSON.parse(jsonInput);
      const result = await getRecommendations({
        orderData: jsonData,
        includeMatchingRecommendations: true,
        includeApprovalRecommendations: true,
        includeSkipConditionRecommendations: true
      });
      setRecommendationResult(result);
    } catch (err: any) {
      console.error('Failed to get recommendations:', err);
    }
  };

  // Handler for adding an identifier to the draft workflow
  const handleAddIdentifierToDraft = useCallback((identifier: {
    name: string;
    jsonPath: string;
    operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'regex' | 'greaterThan' | 'lessThan';
    value?: string;
    regexPattern?: string;
  }) => {
    const newIdentifier: DraftIdentifier = {
      id: crypto.randomUUID(),
      name: identifier.name,
      jsonPath: identifier.jsonPath,
      operator: identifier.operator,
      value: identifier.value,
      regexPattern: identifier.regexPattern,
      isRequired: true,
      weight: 1,
    };

    setDraftWorkflow(prev => ({
      ...prev,
      identifiers: [...prev.identifiers, newIdentifier],
      isExpanded: true, // Auto-expand when adding first identifier
    }));
  }, []);

  // Handler for adding a routing rule to the draft workflow
  const handleAddRoutingRuleToDraft = useCallback((rule: {
    field: string;
    operator: string;
    value: string;
  }) => {
    const newRule: DraftRoutingRule = {
      id: crypto.randomUUID(),
      field: rule.field,
      operator: rule.operator,
      value: rule.value,
    };

    setDraftWorkflow(prev => ({
      ...prev,
      routingRules: [...prev.routingRules, newRule],
      isExpanded: true, // Auto-expand when adding first rule
    }));
  }, []);

  // Handler for creating an order attribute from a discovered field
  const handleCreateOrderAttribute = useCallback((field: DiscoveredField) => {
    setSelectedFieldForAttribute(field);
    setOrderAttributeModalOpen(true);
  }, []);

  const handleCloseOrderAttributeModal = useCallback(() => {
    setOrderAttributeModalOpen(false);
    setSelectedFieldForAttribute(null);
  }, []);

  const handleOrderAttributeCreated = useCallback(() => {
    alert('Order attribute created successfully!');
  }, []);

  // Handler for adding a routing rule to an existing workflow from a discovered field
  const handleAddRoutingRuleToWorkflow = useCallback((field: DiscoveredField) => {
    setSelectedFieldForRoutingRule(field);
    setRoutingRuleModalOpen(true);
  }, []);

  const handleCloseRoutingRuleModal = useCallback(() => {
    setRoutingRuleModalOpen(false);
    setSelectedFieldForRoutingRule(null);
  }, []);

  const handleRoutingRuleAdded = useCallback(() => {
    alert('Routing rule added to workflow successfully!');
  }, []);

  // Handler for creating the workflow from draft
  const handleCreateWorkflow = async () => {
    if (!selectedDivisionId || (draftWorkflow.identifiers.length === 0 && draftWorkflow.routingRules.length === 0) || !draftWorkflow.name) {
      return;
    }

    try {
      // Determine matching mode based on what's configured
      const matchingMode = draftWorkflow.routingRules.length > 0 ? 'routingRules' : 'matchIdentifiers';

      // Step 1: Create the workflow first
      const workflowRequest: CreateWorkflowRequest = {
        name: draftWorkflow.name,
        description: draftWorkflow.description,
        type: 'approval',
        matchingMode: matchingMode,
        matchCriteria: {
          matchIdentifiers: [],
          executionIdentifiers: [],
        },
        workflowSteps: draftWorkflow.workflowSteps,
        routingRules: draftWorkflow.routingRules.map(rule => ({
          id: rule.id,
          field: rule.field,
          operator: rule.operator,
          value: rule.value,
        })),
        settings: {
          autoEscalation: false,
          escalationHours: 24,
          notifications: {
            email: true,
            slack: false,
            sms: false,
          },
          timeoutAction: 'notify',
        },
      };

      const createdWorkflow = await createWorkflowMutation.mutateAsync(workflowRequest);

      // Step 2: Create MatchIdentifier entities for each draft identifier and link to workflow
      let successCount = 0;
      let errorCount = 0;

      for (const draftIdentifier of draftWorkflow.identifiers) {
        try {
          // Map frontend operator names to backend snake_case format
          const operatorMap: Record<string, string> = {
            'equals': 'equals',
            'contains': 'contains',
            'startsWith': 'starts_with',
            'endsWith': 'ends_with',
            'regex': 'matches',
            'greaterThan': 'greater_than',
            'lessThan': 'less_than',
          };
          const backendOperator = operatorMap[draftIdentifier.operator] || 'equals';

          // Create the MatchIdentifier at the division level
          const matchIdentifierRequest: CreateMatchIdentifierRequest = {
            name: draftIdentifier.name,
            description: `Auto-created from simulator: ${draftIdentifier.jsonPath}`,
            type: 'jsonpath', // JSON path matching type
            matchingCriteria: {
              jsonPath: draftIdentifier.jsonPath,
              operator: backendOperator,
              validationRules: draftIdentifier.value ? {
                allowedValues: [draftIdentifier.value],
              } : undefined,
            },
          };

          const createdMatchIdentifier = await createMatchIdentifierMutation.mutateAsync({
            divisionId: selectedDivisionId,
            request: matchIdentifierRequest,
          });

          // Link the MatchIdentifier to the Workflow
          await createWorkflowMatchIdentifierMutation.mutateAsync({
            workflowId: createdWorkflow.id,
            data: {
              matchIdentifierId: createdMatchIdentifier.id,
              isRequired: draftIdentifier.isRequired,
              weight: draftIdentifier.weight,
              priority: 1,
              active: true,
            },
          });

          successCount++;
        } catch (identifierErr: any) {
          console.error(`Failed to create match identifier "${draftIdentifier.name}":`, identifierErr);
          errorCount++;
        }
      }

      // Show result message
      if (errorCount === 0) {
        alert(`Workflow "${createdWorkflow.name}" created with ${successCount} match identifier(s)!`);
      } else {
        alert(`Workflow "${createdWorkflow.name}" created. ${successCount} identifier(s) added successfully, ${errorCount} failed. Check console for details.`);
      }

      navigate(`/workflows/${selectedDivisionId}/${createdWorkflow.id}/edit?tab=match-identifiers`);
      
      // Clear the draft
      setDraftWorkflow({
        name: '',
        description: '',
        identifiers: [],
        routingRules: [],
        workflowSteps: [],
        isExpanded: false,
      });
    } catch (err: any) {
      console.error('Failed to create workflow:', err);
      alert(`Failed to create workflow: ${err?.message || 'Unknown error'}`);
    }
  };

  // Handler for clearing the draft
  const handleClearDraft = () => {
    if (draftWorkflow.identifiers.length > 0 || draftWorkflow.routingRules.length > 0 || draftWorkflow.name) {
      if (!confirm('Are you sure you want to clear the draft workflow?')) {
        return;
      }
    }
    setDraftWorkflow({
      name: '',
      description: '',
      identifiers: [],
      routingRules: [],
      workflowSteps: [],
      isExpanded: false,
    });
  };

  // Support role cannot access the simulator
  if (isSupport) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-2">
              Access Denied
            </h2>
            <p className="text-secondary-600 dark:text-secondary-400">
              The Workflow Simulator is not available to Support users.
            </p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">Workflow Simulator</h1>
          <p className="text-secondary-600 dark:text-secondary-400 mt-1">
            Test workflows and get recommendations for workflow configuration
          </p>
        </div>

        {/* Division Selector + Mode Toggle */}
        <Card className="p-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px] max-w-md">
              <Select
                label="Select Division"
                value={selectedDivisionId}
                onChange={(e) => handleDivisionChange(e.target.value)}
              >
                <option value="">Choose a division...</option>
                {divisions.map(division => (
                  <option key={division.id} value={division.id}>
                    {division.name} {division.code ? `(${division.code})` : ''}
                  </option>
                ))}
              </Select>
            </div>
            
            {/* Mode Toggle */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-1">
                Mode
              </label>
              <div className="flex rounded-lg overflow-hidden border border-secondary-300 dark:border-secondary-600">
                <button
                  onClick={() => setMode('simulate')}
                  className={cn(
                    'px-4 py-2 text-sm font-medium transition-colors',
                    mode === 'simulate'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-700'
                  )}
                >
                  <i className="fa-solid fa-play mr-2" />
                  Simulate
                </button>
                <button
                  onClick={() => setMode('recommend')}
                  className={cn(
                    'px-4 py-2 text-sm font-medium transition-colors border-l border-secondary-300 dark:border-secondary-600',
                    mode === 'recommend'
                      ? 'bg-yellow-500 text-white'
                      : 'bg-white dark:bg-secondary-800 text-secondary-700 dark:text-secondary-200 hover:bg-secondary-50 dark:hover:bg-secondary-700'
                  )}
                >
                  <i className="fa-solid fa-lightbulb mr-2" />
                  Recommend
                </button>
              </div>
            </div>
            
            {mode === 'simulate' && (
              <div className="w-40">
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200 mb-1">
                  Total Amount (optional)
                </label>
                <input
                  type="number"
                  value={totalAmount}
                  onChange={(e) => setTotalAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                />
              </div>
            )}
          </div>
        </Card>

        {/* Main Content */}
        {selectedDivisionId ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Panel - JSON Input */}
            <Card className="p-6">
              {isOrderLoading ? (
                <div className="flex flex-col items-center justify-center py-12 text-secondary-500 dark:text-secondary-400">
                  <div className="w-8 h-8 border-4 border-secondary-200 dark:border-secondary-700 border-t-primary-600 rounded-full animate-spin mb-3" />
                  <p>Loading order data...</p>
                </div>
              ) : (
                <JsonInputPanel
                  value={jsonInput}
                  onChange={setJsonInput}
                  onSubmit={handleRunSimulation}
                  isLoading={isFullLoading || isRecommendationsLoading}
                  error={error}
                  submitLabel={mode === 'simulate' ? 'Run Simulation' : 'Get Recommendations'}
                />
              )}
            </Card>

            {/* Right Panel - Results */}
            <Card className="p-6">
              {mode === 'simulate' ? (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                      Simulation Results
                    </h3>
                    {simulationResult && (
                      <button
                        onClick={() => {
                          setSimulationResult(null);
                          setRecommendationResult(null);
                          setError(null);
                        }}
                        className="text-sm text-secondary-500 dark:text-secondary-400 hover:text-secondary-700"
                      >
                        Clear Results
                      </button>
                    )}
                  </div>
                  <SimulationResultsPanel
                    result={simulationResult}
                    isLoading={isFullLoading}
                    onGetRecommendations={handleGetRecommendationsAfterNoMatch}
                    isLoadingRecommendations={isRecommendationsLoading}
                    recommendations={recommendationResult}
                    onAddIdentifier={handleAddIdentifierToDraft}
                    onCreateOrderAttribute={handleCreateOrderAttribute}
                  />
                </>
              ) : (
                <>
                  {recommendationResult ? (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">
                          Workflow Recommendations
                        </h3>
                        <button
                          onClick={() => {
                            setRecommendationResult(null);
                            setError(null);
                          }}
                          className="text-sm text-secondary-500 dark:text-secondary-400 hover:text-secondary-700"
                        >
                          Clear Results
                        </button>
                      </div>
                      <RecommendationsPanel 
                        result={recommendationResult} 
                        onAddIdentifier={handleAddIdentifierToDraft}
                        onAddRoutingRule={handleAddRoutingRuleToDraft}
                        onCreateOrderAttribute={handleCreateOrderAttribute}
                        onAddRoutingRuleToWorkflow={handleAddRoutingRuleToWorkflow}
                      />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-secondary-500 dark:text-secondary-400">
                      {isRecommendationsLoading ? (
                        <>
                          <div className="w-8 h-8 border-4 border-secondary-200 dark:border-secondary-700 border-t-primary-600 rounded-full animate-spin mb-3" />
                          <p>Analyzing order data...</p>
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-lightbulb text-4xl mb-4 text-yellow-400" />
                          <h3 className="text-lg font-medium text-secondary-700 dark:text-secondary-200 mb-2">
                            Get Workflow Recommendations
                          </h3>
                          <p className="text-center max-w-md">
                            Paste your order JSON and click "Get Recommendations" to analyze
                            the data and get suggestions for workflow configuration.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        ) : (
          <Card className="p-12">
            <div className="text-center">
              <svg className="w-16 h-16 text-secondary-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                Select a Division
              </h2>
              <p className="text-secondary-600 dark:text-secondary-400">
                Choose a division above to start simulating workflows
              </p>
            </div>
          </Card>
        )}

        {/* Draft Workflow Builder - shows when there are draft identifiers or expanded */}
        {selectedDivisionId && (draftWorkflow.identifiers.length > 0 || draftWorkflow.isExpanded) && (
          <DraftWorkflowBuilder
            draft={draftWorkflow}
            onChange={setDraftWorkflow}
            onCreateWorkflow={handleCreateWorkflow}
            onClear={handleClearDraft}
            isCreating={createWorkflowMutation.isPending}
            divisionId={selectedDivisionId}
          />
        )}

        {/* Help Section */}
        <Card className="p-6 bg-secondary-50 dark:bg-secondary-900">
          <h3 className="text-sm font-semibold text-secondary-900 dark:text-white mb-3">
            {mode === 'simulate' ? 'How to use the Simulator' : 'How to use Recommendations'}
          </h3>
          {mode === 'simulate' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-medium">
                  1
                </span>
                <div>
                  <h4 className="font-medium text-secondary-900 dark:text-white">Enter JSON Data</h4>
                  <p className="text-secondary-600 dark:text-secondary-400">
                    Paste or type the JSON document that would trigger a workflow (e.g., purchase order data)
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-medium">
                  2
                </span>
                <div>
                  <h4 className="font-medium text-secondary-900 dark:text-white">Run Simulation</h4>
                  <p className="text-secondary-600 dark:text-secondary-400">
                    Click "Run Simulation" to see which workflow would match your data
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-medium">
                  3
                </span>
                <div>
                  <h4 className="font-medium text-secondary-900 dark:text-white">Review Results</h4>
                  <p className="text-secondary-600 dark:text-secondary-400">
                    See the matched workflow, steps, and which users would be assigned as approvers
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400 flex items-center justify-center text-xs font-medium">
                  1
                </span>
                <div>
                  <h4 className="font-medium text-secondary-900 dark:text-white">Paste Order Data</h4>
                  <p className="text-secondary-600 dark:text-secondary-400">
                    Enter a sample order JSON to analyze. The system will identify key fields and patterns.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400 flex items-center justify-center text-xs font-medium">
                  2
                </span>
                <div>
                  <h4 className="font-medium text-secondary-900 dark:text-white">Get Recommendations</h4>
                  <p className="text-secondary-600 dark:text-secondary-400">
                    The engine analyzes your data and suggests matching criteria, steps, and skip conditions.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-400 flex items-center justify-center text-xs font-medium">
                  3
                </span>
                <div>
                  <h4 className="font-medium text-secondary-900 dark:text-white">Apply to Workflow</h4>
                  <p className="text-secondary-600 dark:text-secondary-400">
                    Review recommendations and apply them to create or configure your workflow.
                  </p>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Order Attribute Creation Modal */}
      {selectedFieldForAttribute && (
        <CreateOrderAttributeModal
          isOpen={orderAttributeModalOpen}
          field={selectedFieldForAttribute}
          divisionId={selectedDivisionId || ''}
          onClose={handleCloseOrderAttributeModal}
          onSuccess={handleOrderAttributeCreated}
        />
      )}

      {/* Add Routing Rule to Workflow Modal */}
      {selectedFieldForRoutingRule && (
        <AddRoutingRuleToWorkflowModal
          isOpen={routingRuleModalOpen}
          field={selectedFieldForRoutingRule}
          divisionId={selectedDivisionId || ''}
          onClose={handleCloseRoutingRuleModal}
          onSuccess={handleRoutingRuleAdded}
        />
      )}
    </SidebarLayout>
  );
}
